import { Box, Stack } from '@mantine/core'
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { startTransition, useLayoutEffect, useRef } from 'react'
import {
  getBoggleGameModeDefinition,
  isBogglePublicGameModeParam,
  type BoggleGameModeId,
} from '~/boggle/gameModes'
import { GamePageHeader } from '~/boggle/ui/chrome/AppLayout'
import { GameModeCatalogScreen } from '~/boggle/ui/screens/GameModeCatalogScreen'
import { RunScreen } from '~/boggle/ui/run/RunScreen'
import { GameStagingScreen } from '~/boggle/ui/screens/GameStagingScreen'
import { readBoggleGamesAutoStartFromHistoryState } from '~/boggle/ui/storage/gamesRouteState'
import { useBoggleModeSession } from '~/boggle/ui/state/ModeSession'
import {
  isBoggleDebugSearchEnabled,
  readBoggleDebugSeed,
} from '~/boggle/ui/state/debugSearch'

export const Route = createFileRoute('/games/$gameModeId')({
  head: ({ params }) => ({
    meta: [
      { title: 'Game — Patter' },
      {
        name: 'description',
        content: `Play Patter ${getBoggleGameModeDefinition(params.gameModeId as BoggleGameModeId).name}`,
      },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  component: GamesModeRoute,
})

function GamesCatalogForInvalidSlug({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>
}) {
  const { clearRouteHostedGameMode } = useBoggleModeSession()

  useLayoutEffect(() => {
    clearRouteHostedGameMode()
  }, [clearRouteHostedGameMode])

  return (
    <Stack gap={0} h="100%" mih={0}>
      <GamePageHeader />
      <Box flex={1} mih={0}>
        <GameModeCatalogScreen
          onBack={() => {
            void navigate({ to: '/' })
          }}
          onSelect={(gameModeId: BoggleGameModeId) => {
            void navigate({
              to: '/games/$gameModeId',
              params: { gameModeId },
              state: { boggleAutoStartStandardRun: true },
            })
          }}
        />
      </Box>
    </Stack>
  )
}

function GamesModeRoute() {
  const navigate = useNavigate()
  const { gameModeId: gameModeParam } = Route.useParams()

  if (!isBogglePublicGameModeParam(gameModeParam)) {
    return <GamesCatalogForInvalidSlug navigate={navigate} />
  }

  return <GamesModeFlow gameModeId={gameModeParam} />
}

function GamesModeFlow({ gameModeId }: { gameModeId: BoggleGameModeId }) {
  const navigate = useNavigate()
  const navigateFromMode = Route.useNavigate()
  const {
    clearRouteHostedGameMode,
    endStandardRun,
    isStandardRunActive,
    newStandardGame,
    persistGameModeFromGamesRoute,
    standardSeed,
    startStandardRun,
  } = useBoggleModeSession()
  const shouldAutoStartStandardRun = useRouterState({
    select: state => readBoggleGamesAutoStartFromHistoryState(state.location.state),
  })
  const routerSearch = useRouterState({
    select: state =>
      ((state.location as { search?: Record<string, unknown> }).search ?? {}) as Record<
        string,
        unknown
      >,
  })
  const autoStartConsumedRef = useRef(false)
  const shouldDebugAutoStartRun = isBoggleDebugSearchEnabled(routerSearch)
  const debugSeed = shouldDebugAutoStartRun ? readBoggleDebugSeed(routerSearch) : null

  useLayoutEffect(() => {
    autoStartConsumedRef.current = false
  }, [gameModeId])

  useLayoutEffect(() => {
    persistGameModeFromGamesRoute(gameModeId)
  }, [gameModeId, persistGameModeFromGamesRoute])

  useLayoutEffect(() => {
    if (
      isStandardRunActive ||
      (!shouldAutoStartStandardRun && !shouldDebugAutoStartRun) ||
      autoStartConsumedRef.current
    ) {
      return
    }
    autoStartConsumedRef.current = true
    startStandardRun(gameModeId, debugSeed ?? undefined)
    void navigateFromMode({
      to: '.',
      replace: true,
      params: { gameModeId },
      search: prev => prev,
      state: prev => ({
        ...(typeof prev === 'object' && prev !== null ? prev : {}),
        boggleAutoStartStandardRun: undefined,
      }),
    })
  }, [
    debugSeed,
    gameModeId,
    isStandardRunActive,
    navigateFromMode,
    shouldAutoStartStandardRun,
    shouldDebugAutoStartRun,
    startStandardRun,
  ])

  const definition = getBoggleGameModeDefinition(gameModeId)
  const onExitStandardRun = () => {
    endStandardRun()
    void navigate({ to: '/games' })
  }

  if (isStandardRunActive) {
    return (
      <RunScreen
        gameModeId={gameModeId}
        onExitRun={onExitStandardRun}
        onNewGame={newStandardGame}
        runKind="standard"
        seed={standardSeed}
      />
    )
  }

  return (
    <Stack gap={0} h="100%" mih={0}>
      <GamePageHeader />
      <Box flex={1} mih={0}>
        <GameStagingScreen
          gameMode={definition}
          onBack={() => {
            clearRouteHostedGameMode()
            void navigate({ to: '/games' })
          }}
          onStart={() => startStandardRun(gameModeId)}
        />
      </Box>
    </Stack>
  )
}
